// Module 9 — Your Next Move
// ./index.ts owns this module's key and display name; this file owns its
// action items.

// Libs
import { useParams } from 'react-router';

// RDS
import Heading from '@ramsey-design-system/heading';
import Icon from '@ramsey-design-system/icon';
import { StarIcon } from '@ramsey-design-system/icons';
import Text from '@ramsey-design-system/text';

// Components
import QrCodePrompt from '../QrCodePrompt';

// Utils
import { PATHS } from '~fpu/config/constants';
import storeConfig from '~fpu/config/storeConfig';

// Types
import { WorkshopActionItemDefinition } from '../../types';

const ACCOMPLISHMENTS: string[] = [
  'Identified your income and what you actually bring home each month.',
  'Listed your expenses and prioritized the Four Walls first.',
  'Faced your debt and made a plan to tackle it.',
  'Discovered the best way to track your transactions.',
  'Learned how to hold a monthly budget meeting to stay on track.'
];

function Congratulations() {
  return (
    <div className="flex flex-col gap-4">
      <div className="gap-md flex items-center">
        <img
          src="https://cdn.ramseysolutions.net/media/ramsey-plus/financial-peace/coordinator/dashboard/budget-workshop-assets/celebration-icon.png"
          alt=""
          className="h-12 w-12 shrink-0"
        />
        <Heading level="3" size="large">
          Congrats! You did it!
        </Heading>
      </div>

      <Text className="text-4 leading-lg">
        You showed up, did the work, and walked through every step of building a
        budget from scratch.
      </Text>

      <Text className="text-4 leading-lg">
        Here&apos;s what you accomplished today:
      </Text>

      <ul className="text-4 leading-lg ml-2 list-disc">
        {ACCOMPLISHMENTS.map((accomplishment) => (
          <li key={accomplishment}>{accomplishment}</li>
        ))}
      </ul>
    </div>
  );
}

function TheSevenDayChallenge() {
  return (
    <div className="flex flex-col gap-4">
      <img
        src="https://cdn.ramseysolutions.net/media/ramsey-plus/financial-peace/coordinator/dashboard/budget-workshop-assets/everydollar-banner.png"
        alt="Ramsey EveryDollar"
        className="h-auto w-full max-w-[220px]"
      />

      <Heading level="3" size="small">
        Your 7 Day Tracking Challenge!
      </Heading>

      <Text className="text-4 leading-lg">
        Your Financial Peace All Access membership includes the EveryDollar app.
        Download it, set up your account, and enter the numbers from today.
      </Text>

      <Text className="text-4 leading-lg">
        Then for the next seven days, track every dollar you spend. You
        can&apos;t manage what you don&apos;t measure — and seven days is all it
        takes to see where your money is really going.
      </Text>
    </div>
  );
}

function LevelUpWithFpu() {
  return (
    <div className="flex flex-col gap-4">
      <img
        src="https://cdn.ramseysolutions.net/media/ramsey-plus/financial-peace/coordinator/dashboard/budget-workshop-assets/fpu-banner.png"
        alt="Ramsey Financial Peace"
        className="h-auto w-full max-w-[220px]"
      />

      <Text className="text-4 leading-lg">
        Did you know that families who go through Financial Peace University see
        an average of $8,000 in financial turnaround? That could be you next.
      </Text>

      <Text className="text-4 leading-lg">
        Ask your organization about upcoming FPU classes — or if there
        isn&apos;t one yet, ask about getting one started.
      </Text>
    </div>
  );
}

function YourVoiceMatters() {
  const { workshopId } = useParams() as { workshopId: string };
  const feedbackUrl = `${storeConfig.appUrl}${PATHS.MEMBER_DASHBOARD.WORKSHOP_FEEDBACK(workshopId)}`;

  return (
    <div className="flex flex-col gap-6">
      <div
        className="gap-md flex items-center justify-center text-amber-50"
        aria-hidden="true"
      >
        {Array.from({ length: 5 }, (_, i) => (
          <Icon key={i} icon={StarIcon} size="xlarge" />
        ))}
      </div>

      <QrCodePrompt
        activityUrl={feedbackUrl}
        slug="workshop-feedback"
        dialogTitle="Workshop Feedback QR Code"
      >
        <Text className="text-5 leading-lg font-bold">
          Your voice matters. Take a moment to reflect.
        </Text>
        <Text className="text-4 leading-lg">
          Share how today&apos;s workshop connects to your goals, your
          challenges, or a moment that just made sense.
        </Text>
      </QrCodePrompt>
    </div>
  );
}

const actionItems: WorkshopActionItemDefinition[] = [
  { title: 'Congratulations!', Content: Congratulations },
  { title: 'The 7 Day Challenge', Content: TheSevenDayChallenge },
  { title: 'Level Up with FPU', Content: LevelUpWithFpu },
  { title: 'Your Voice Matters', Content: YourVoiceMatters }
];

export default actionItems;
